import { useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import dayjs from "lib/dayjs"

import {
  useLazyGetIntelligenceReportingPeriodQuery,
  useLazyGetIntelligenceReportsQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"
import { Spinner } from "components/common/Spinner"
import {
  PriorityBadge,
  type PriorityBadgeVariant,
} from "pages/contractor/IntelligenceReportsPage/components/PriorityBadge"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  LoadingContainer,
  NoAlertsText,
  ReportItem,
  ReportPeriod,
  ReportStatusWrapper,
  StyledWidgetButton,
  Widget,
  WidgetButton,
  WidgetContent,
  WidgetTitle,
} from "../styled"

const REPORTS_TO_SHOW = 2

const toPriorityVariant = (priority: string): PriorityBadgeVariant => {
  const normalized = priority.toLowerCase()
  return normalized === "high" ? "high" : normalized === "medium" ? "medium" : "low"
}

export const IntelligenceReportWidget = () => {
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const selectedDevice = devices[currentDevice]
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.IntelligenceReports) : false

  const [fetchReportingPeriod, { data: periodData }] = useLazyGetIntelligenceReportingPeriodQuery()
  const [fetchReports, { data, isFetching }] = useLazyGetIntelligenceReportsQuery()

  useEffect(() => {
    if (!hasAccess) return
    fetchReportingPeriod()
  }, [hasAccess, fetchReportingPeriod])

  useEffect(() => {
    if (!selectedDevice?.sn || !hasAccess || !periodData) return
    fetchReports({
      sn: selectedDevice.sn,
      per_page: REPORTS_TO_SHOW,
      page: 1,
      start_ts_utc: periodData.data.start_ts_utc,
      end_ts_utc: periodData.data.end_ts_utc,
    } as UITypes.Params)
  }, [selectedDevice?.sn, hasAccess, fetchReports, periodData])

  const reports = useMemo(() => (data?.data || []).slice(0, REPORTS_TO_SHOW), [data?.data])

  const handleViewAll = () => {
    navigate(`/customers/${id}/reports`)
  }

  if (!selectedDevice) {
    return null
  }

  return (
    <Widget>
      <WidgetTitle>Intelligence Report</WidgetTitle>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.IntelligenceReports}>
        <WidgetContent>
          {isFetching ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : reports.length === 0 ? (
            <NoAlertsText>No reports for this device</NoAlertsText>
          ) : (
            reports.map(report => (
              <ReportItem key={report.report_id}>
                <ReportPeriod>
                  {dayjs(report.start_date).format("MM.DD")} – {dayjs(report.end_date).format("MM.DD.YYYY")}
                </ReportPeriod>
                <ReportStatusWrapper>
                  <PriorityBadge variant={toPriorityVariant(report.priority)}>{report.priority}</PriorityBadge>
                </ReportStatusWrapper>
              </ReportItem>
            ))
          )}
        </WidgetContent>
        <WidgetButton>
          <StyledWidgetButton onClick={handleViewAll} size="auto" color="blueBordered">
            View All
          </StyledWidgetButton>
        </WidgetButton>
      </RestrictedAccessWidget>
    </Widget>
  )
}
